import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApolloClient, useMutation } from '@apollo/client';
import { useSelector, useDispatch } from 'react-redux';
import { UPDATE_PROFILE, CHANGE_PASSWORD } from '../../graphql/mutations';
import { ME } from '../../graphql/queries';
import PullToRefresh from '../../components/PullToRefresh/PullToRefresh';
import { logout } from '../../store/slices/authSlice';
import { clearCurrentUser, setCurrentUser } from '../../store/slices/mateSlice';
import BackHeader from '../../components/BackHeader/BackHeader';
import Button from '../../components/Button/Button';
import Card from '../../components/Card/Card';
import Notification from '../../components/Notification/Notification';
import Spacer from '../../components/Spacer/Spacer';
import useNotification from '../../hooks/useNotification';
import PushNotificationsSettings from '../../components/PushNotificationsSettings/PushNotificationsSettings';
import '../AdminPanel/MateManagement/MateDetail/MateDetail.css';
import './Profile.css';

const Profile = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const apolloClient = useApolloClient();
  const user = useSelector((state) => state.mate.currentUser);
  const [name, setName] = useState(user?.name || '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const { showNotification, notificationProps } = useNotification();

  useEffect(() => {
    setName(user?.name || '');
  }, [user?.name]);

  const displayName = user?.name || 'Profil';
  const initials = useMemo(() => {
    const n = displayName.trim();
    if (!n) return '?';
    return n
      .split(/\s+/)
      .map((part) => part[0])
      .join('')
      .toUpperCase()
      .slice(0, 3);
  }, [displayName]);

  const [updateProfile, { loading: profileLoading }] = useMutation(UPDATE_PROFILE, {
    onCompleted: (data) => {
      dispatch(setCurrentUser(data.updateProfile));
      showNotification('Profil opdateret!', 'success');
    },
    onError: (error) => {
      showNotification(error.message || 'Fejl ved opdatering af profil', 'error');
    }
  });

  const [changePassword, { loading: passwordLoading }] = useMutation(CHANGE_PASSWORD, {
    onCompleted: () => {
      showNotification('Password ændret!', 'success');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    },
    onError: (error) => {
      showNotification(error.message || 'Fejl ved ændring af password', 'error');
    }
  });

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      showNotification('Navn er påkrævet', 'error');
      return;
    }

    try {
      await updateProfile({
        variables: { name: name.trim() }
      });
    } catch (error) {
      console.error('Profile update error:', error);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();

    if (!currentPassword || !newPassword || !confirmPassword) {
      showNotification('Alle felter er påkrævet', 'error');
      return;
    }

    if (newPassword.length < 8) {
      showNotification('Nyt password skal være mindst 8 tegn langt', 'error');
      return;
    }

    if (newPassword !== confirmPassword) {
      showNotification('Nye passwords matcher ikke', 'error');
      return;
    }

    try {
      await changePassword({
        variables: {
          currentPassword,
          newPassword
        }
      });
    } catch (error) {
      console.error('Password change error:', error);
    }
  };

  const isAdmin = user?.role === 'admin';
  const savedName = user?.name || '';

  const refreshProfile = async () => {
    const { data } = await apolloClient.query({ query: ME, fetchPolicy: 'network-only' });
    const me = data?.me;
    if (!me) return;
    dispatch(setCurrentUser(me));
    if (name === savedName) setName(me.name || '');
  };

  const handleLogout = async () => {
    await apolloClient.clearStore();
    localStorage.removeItem('championsLeagueGame');
    localStorage.removeItem('championsLeagueRealResults');
    localStorage.removeItem('championsLeaguePhaseLocks');
    dispatch(logout());
    dispatch(clearCurrentUser());
    navigate('/');
  };

  return (
    <PullToRefresh onRefresh={refreshProfile}>
    <div className="profile-page">
      <Notification {...notificationProps} />

      <div className="profile-page__inner">
        <BackHeader
          eyebrow="Svirr"
          title="Profil"
          subtitle={isAdmin ? 'Administrator' : 'Bruger'}
          onBack={() => navigate('/')}
          backLabel="Tilbage til forsiden"
        />

        <div className="mate-detail-content profile-scroll-content">
          <div className="mate-detail-wrapper">
          <Card className="mate-detail-section-card" padding="medium" hover={false}>
            <div className="mate-detail-identity">
              <div className={`mate-detail-avatar ${isAdmin ? 'admin' : ''}`}>{initials}</div>
              <div className="mate-detail-identity-info">
                <h2 className="mate-detail-name">{displayName}</h2>
                <p className="mate-detail-email-role">{user?.email || '–'}</p>
                <p className="mate-detail-email-role">{isAdmin ? 'Administrator' : 'Bruger'}</p>
              </div>
            </div>
          </Card>

          <Spacer size="20px" />

          <div className="mate-detail-section-cards profile-section-stack">
            <Card className="mate-detail-section-card" padding="medium" hover={false}>
              <h2 className="mate-detail-section-title">Oplysninger</h2>
              <form onSubmit={handleProfileUpdate} className="mate-detail-form mate-detail-form-row">
                <div className="mate-detail-form-row-item">
                  <label htmlFor="email">Email</label>
                  <span className="mate-detail-value">
                    <input
                      type="email"
                      id="email"
                      value={user?.email || ''}
                      disabled
                      className="form-input disabled"
                    />
                    <small className="form-help">Email kan ikke ændres endnu</small>
                  </span>
                </div>

                <div className="mate-detail-form-row-item">
                  <label htmlFor="name">Navn</label>
                  <span className="mate-detail-value mate-detail-value--input">
                    <input
                      type="text"
                      id="name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="form-input"
                      placeholder="Indtast dit navn"
                      required
                    />
                  </span>
                </div>

                <div className="mate-detail-actions profile-card-actions">
                  <Button
                    type="submit"
                    disabled={profileLoading || !name.trim()}
                    loading={profileLoading}
                    title={profileLoading ? 'Gemmer...' : 'Gem ændringer'}
                  />
                </div>
              </form>
            </Card>

            <Card className="mate-detail-section-card" padding="medium" hover={false}>
              <h2 className="mate-detail-section-title">Skift password</h2>
              <form onSubmit={handlePasswordChange} className="mate-detail-form mate-detail-form-row">
                <div className="mate-detail-form-row-item">
                  <label htmlFor="currentPassword">Nuværende password</label>
                  <span className="mate-detail-value mate-detail-value--input">
                    <input
                      type="password"
                      id="currentPassword"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      className="form-input"
                      placeholder="Nuværende password"
                      required
                    />
                  </span>
                </div>

                <div className="mate-detail-form-row-item">
                  <label htmlFor="newPassword">Nyt password</label>
                  <span className="mate-detail-value mate-detail-value--input">
                    <input
                      type="password"
                      id="newPassword"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="form-input"
                      placeholder="Nyt password"
                      required
                      minLength="8"
                    />
                    <small className="form-help">Mindst 8 tegn</small>
                  </span>
                </div>

                <div className="mate-detail-form-row-item">
                  <label htmlFor="confirmPassword">Bekræft nyt password</label>
                  <span className="mate-detail-value mate-detail-value--input">
                    <input
                      type="password"
                      id="confirmPassword"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="form-input"
                      placeholder="Bekræft password"
                      required
                    />
                  </span>
                </div>

                <div className="mate-detail-actions profile-card-actions">
                  <Button
                    type="submit"
                    disabled={passwordLoading || !currentPassword || !newPassword || !confirmPassword}
                    loading={passwordLoading}
                    title={passwordLoading ? 'Skifter...' : 'Skift password'}
                  />
                </div>
              </form>
            </Card>

            <Card className="mate-detail-section-card" padding="medium" hover={false}>
              <h2 className="mate-detail-section-title">Notifikationer</h2>
              <PushNotificationsSettings />
            </Card>

            <Card className="mate-detail-section-card profile-logout-card" padding="medium" hover={false}>
              <h2 className="mate-detail-section-title">Log ud</h2>
              <p className="profile-logout-text">Log ud af Svirr på denne enhed.</p>
              <div className="mate-detail-actions profile-card-actions">
                <Button color="danger" title="Log ud" onClick={handleLogout} />
              </div>
            </Card>
          </div>
        </div>
        </div>
      </div>
    </div>
    </PullToRefresh>
  );
};

export default Profile;
